import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { LocalStorageKeys } from '@constants/enums';
import { ROUTES } from '@configs/routes';
import { AUTH_SCENARIOS } from '@constants/auth.constants';
import authSvc from '../../../services/auth/authSvc';
import {
  legacyForgotPasswordSchema,
  LegacyForgotPasswordData,
} from '../../../schemas/auth.schemas';
import useAuthErrorHandler from '../../shared/hooks/useAuthErrorHandler';
import { UseLegacyForgotPasswordReturn } from '../types';

const useLegacyForgotPassword = (): UseLegacyForgotPasswordReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getErrorMessage } = useAuthErrorHandler('forgot-password');

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<LegacyForgotPasswordData>({
    resolver: zodResolver(legacyForgotPasswordSchema),
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });

  const { register, handleSubmit, formState, setValue } = form;

  const getFieldError = useCallback(
    (fieldName: keyof LegacyForgotPasswordData): string => {
      const fieldError = formState.errors[fieldName];
      if (!fieldError?.message) return '';
      return t(fieldError.message);
    },
    [formState.errors, t]
  );

  const handleBackToLogin = useCallback(() => {
    navigate(ROUTES.AUTH.LOGIN);
  }, [navigate]);

  const onSubmit = useCallback(
    async (data: LegacyForgotPasswordData) => {
      try {
        setIsLoading(true);
        setError('');
        setSuccess(false);

        const response = await authSvc.legacyForgotPassword(data.email);

        if (response.status === 200) {
          setSuccess(true);
          localStorage.setItem(
            LocalStorageKeys.RESET_EMAIL_PASSWORD,
            data.email
          );
          setTimeout(
            () =>
              navigate(`${ROUTES.AUTH.LOGIN}?action=${AUTH_SCENARIOS.RESET}`),
            2000
          );
        }
      } catch (err: any) {
        console.error('Legacy forgot password error:', err);
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, getErrorMessage]
  );

  useEffect(() => {
    const savedEmail = localStorage.getItem(
      LocalStorageKeys.RESET_EMAIL_PASSWORD
    );
    if (savedEmail) {
      setValue('email', savedEmail, { shouldValidate: true });
    }
  }, [setValue]);

  return {
    register,
    handleSubmit,
    formState,
    success,
    error,
    isLoading,
    handleBackToLogin,
    onSubmit,
    getFieldError,
  };
};

export default useLegacyForgotPassword;
